/**
 * Valida un NIF, CIF o NIE español.
 * @param value El número a validar.
 * @returns `true` si es válido, `false` si no.
 */
export function isValidNifCif(value: string): boolean {
  if (!value) return false;
  const str = value.toUpperCase().trim();

  if (!str.match(/^[A-Z]\d{7}[A-Z0-9]$|^\d{8}[A-Z]$|^[XYZ]\d{7}[A-Z]$/)) {
    return false;
  }

  // NIF (Personas físicas)
  if (str.match(/^\d{8}[A-Z]$/)) {
    const dni = str.substring(0, 8);
    const letter = str.substring(8, 9);
    const validLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
    return letter === validLetters.charAt(parseInt(dni, 10) % 23);
  }

  // NIE (Extranjeros)
  if (str.match(/^[XYZ]/)) {
    const niePrefix = str.charAt(0);
    let nieNumber = str.substring(1, 8);
    const prefixMap: { [key: string]: string } = { X: '0', Y: '1', Z: '2' };
    nieNumber = (prefixMap[niePrefix] || '') + nieNumber;
    const validLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
    const calculatedLetter = validLetters.charAt(parseInt(nieNumber, 10) % 23);
    return str.charAt(8) === calculatedLetter;
  }

  // CIF (Empresas)
  const firstChar = str.charAt(0);
  const digits = str.substring(1, 8);
  const control = str.charAt(8);
  let sum = 0;

  for (let i = 0; i < digits.length; i++) {
    const digit = parseInt(digits[i], 10);
    if (i % 2 !== 0) { // Posiciones pares (2, 4, 6)
      sum += digit;
    } else { // Posiciones impares (1, 3, 5, 7)
      const product = digit * 2;
      sum += product > 9 ? Math.floor(product / 10) + (product % 10) : product;
    }
  }

  const controlDigit = (10 - (sum % 10)) % 10;
  const controlStr = controlDigit.toString();
  // Tabla correcta para letras de control CIF: J=0, A=1, B=2, C=3, D=4, E=5, F=6, G=7, H=8, I=9
  const controlLetters = "JABCDEFGHI";
  const controlLetter = controlLetters.charAt(controlDigit);

  const isPQS = 'PQS'.indexOf(firstChar) !== -1;
  const isABEH = 'ABEH'.indexOf(firstChar) !== -1;
  const isKLMN = 'KLMN'.indexOf(firstChar) !== -1;

  if (isPQS) {
    // P (Ayuntamientos), Q (Organismos autónomos), S (Órganos administración)
    return control === controlLetter;
  } else if (isABEH) {
    // A (Sociedades anónimas), B (Sociedades limitadas), E (Comunidades propietarios), H (Comunidades de bienes)
    return control === controlStr;
  } else if (isKLMN) {
    // K (Formas jurídicas españolas), L (Sociedades extranjeras), M (Entidades religiosas), N (Entidades extranjeras)
    return control === controlStr;
  } else {
    // Para otros tipos (C, D, F, G, J, N, R, U, V, W) puede ser letra o número
    return control === controlStr || control === controlLetter;
  }
}

/**
 * Valida un IBAN (International Bank Account Number).
 * @param value El IBAN a validar.
 * @returns `true` si es válido, `false` si no.
 */
export function isValidIban(value: string): boolean {
  if (!value) return false;
  const iban = value.toUpperCase().replace(/\s/g, '');

  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(iban)) {
    return false;
  }

  const rearranged = iban.substring(4) + iban.substring(0, 4);
  
  const numeric = Array.from(rearranged).map(char => {
      const code = char.charCodeAt(0);
      // 65-90 are A-Z
      return code >= 65 && code <= 90 ? (code - 55).toString() : char;
  }).join('');
  
  try {
      // Use BigInt for calculations as the number can be very large
      const num = BigInt(numeric);
      return num % 97n === 1n;
  } catch (e) {
      // This might happen if the string is not purely numeric after conversion
      return false;
  }
}
